import { useCallback } from 'react';
import { toast } from 'sonner';
import { StoreCore } from '../types/store';
import { Animal, Family, FamilyGoal, FamilyRules, InviteStatus, Member, ModuleKey, PlanKey, Reward, TaskTemplate } from '../types/family';
import { ChangeEntry, Effective, ScheduledPatch } from '../types/shared';
import { uid, withFamily } from '../utils/db';
import { applyPatch } from '../utils/familyPatch';
import { buildFamily, WizardInput } from '../utils/familyFactory';
import { instrumentTask, planItem, readingTask, sportTask } from '../utils/templateBuilders';

export function useFamilyEditing(core: StoreCore) {
  const { setDb, family, viewer, isAdmin } = core;
  const fid = family.id;

  const log = useCallback(
    (area: string, text: string, effective: Effective = 'takoj', patch?: ScheduledPatch): ChangeEntry => ({
      id: uid('ch'),
      familyId: fid,
      by: viewer.id,
      at: Date.now(),
      area,
      text,
      effective,
      status: effective === 'takoj' ? 'uveljavljeno' : 'nacrtovano',
      patch: effective === 'takoj' ? undefined : patch,
    }),
    [fid, viewer.id]
  );

  /** Takojšnja sprememba (imena, dodelitve, moduli …). */
  const edit = useCallback(
    (area: string, text: string, fn: (f: Family) => Family) => {
      if (!isAdmin) return;
      setDb((db) => ({ ...withFamily(db, fid, fn), changelog: [log(area, text), ...db.changelog] }));
    },
    [isAdmin, setDb, fid, log]
  );

  /** Sprememba točkovnika ali obveznih ciljev – velja takoj ali od naslednjega tedna. */
  const commit = useCallback(
    (area: string, text: string, patch: ScheduledPatch, effective: Effective) => {
      if (!isAdmin) return;
      setDb((db) => {
        const entry = log(area, text, effective, patch);
        const next = effective === 'takoj' ? withFamily(db, fid, (f) => applyPatch(f, patch)) : db;
        return { ...next, changelog: [entry, ...next.changelog] };
      });
      toast.success(effective === 'takoj' ? 'Shranjeno in uveljavljeno.' : 'Shranjeno – velja od naslednjega tedna.');
    },
    [isAdmin, setDb, fid, log]
  );

  const applyScheduled = useCallback(
    (changeId: string) =>
      setDb((db) => {
        const entry = db.changelog.find((c) => c.id === changeId);
        if (!entry || entry.status !== 'nacrtovano' || !entry.patch) return db;
        const patch = entry.patch;
        const next = withFamily(db, entry.familyId, (f) => applyPatch(f, patch));
        return { ...next, changelog: next.changelog.map((c) => (c.id === changeId ? { ...c, status: 'uveljavljeno' } : c)) };
      }),
    [setDb]
  );

  const renameFamily = useCallback((name: string) => edit('Družina', `Ime družine: ${name}`, (f) => ({ ...f, name })), [edit]);

  const saveMember = useCallback(
    (m: Member) => {
      const exists = family.members.some((x) => x.id === m.id);
      if (exists) {
        const admins = family.members.filter((x) => x.role === 'skrbnik' && !x.archived && x.id !== m.id);
        if (m.role !== 'skrbnik' && admins.length === 0) {
          toast.error('Družina potrebuje vsaj enega skrbnika.');
          return;
        }
      }
      edit('Člani', exists ? `Urejen član: ${m.name}` : `Dodan član: ${m.name}`, (f) => ({
        ...f,
        members: exists ? f.members.map((x) => (x.id === m.id ? m : x)) : [...f.members, m],
        plans: exists ? f.plans : [...f.plans, { memberId: m.id, items: [] }],
      }));
    },
    [family.members, edit]
  );

  const archiveMember = useCallback(
    (id: string) => {
      const m = family.members.find((x) => x.id === id);
      if (!m) return;
      if (m.role === 'skrbnik' && family.members.filter((x) => x.role === 'skrbnik' && !x.archived).length <= 1) {
        toast.error('Zadnjega skrbnika ni mogoče arhivirati.');
        return;
      }
      edit('Člani', `Arhiviran član: ${m.name} (zgodovina ostane)`, (f) => ({
        ...f,
        members: f.members.map((x) => (x.id === id ? { ...x, archived: !x.archived } : x)),
      }));
    },
    [family.members, edit]
  );

  const setInvite = useCallback(
    (id: string, invite: InviteStatus) =>
      edit('Vabila', `Vabilo: ${family.members.find((m) => m.id === id)?.name} → ${invite}`, (f) => ({
        ...f,
        members: f.members.map((m) => (m.id === id ? { ...m, invite } : m)),
      })),
    [edit, family.members]
  );

  const saveTemplate = useCallback(
    (t: TaskTemplate, effective: Effective) => {
      const prev = family.templates.find((x) => x.id === t.id);
      if (!prev) {
        edit('Naloge', `Nova naloga: ${t.title}`, (f) => ({ ...f, templates: [...f.templates, t] }));
        return;
      }
      const valueChanged = prev.baseValue !== t.baseValue || prev.targetMinutes !== t.targetMinutes;
      const { baseValue, targetMinutes, ...rest } = t;
      edit('Naloge', `Urejena naloga: ${t.title}`, (f) => ({
        ...f,
        templates: f.templates.map((x) => (x.id === t.id ? { ...x, ...rest } : x)),
      }));
      if (valueChanged) {
        commit('Točkovnik', `${t.title}: vrednost ${prev.baseValue ?? 0} → ${baseValue ?? 0}, cilj ${prev.targetMinutes ?? '–'} → ${targetMinutes ?? '–'} min`, { target: 'template', id: t.id, patch: { baseValue, targetMinutes } }, effective);
      }
    },
    [family.templates, edit, commit]
  );

  const deleteTemplate = useCallback(
    (id: string) => edit('Naloge', `Odstranjena naloga: ${family.templates.find((t) => t.id === id)?.title}`, (f) => ({ ...f, templates: f.templates.filter((t) => t.id !== id) })),
    [edit, family.templates]
  );

  const togglePersonalGoal = useCallback(
    (memberId: string, key: Extract<PlanKey, 'branje' | 'sport'>, on: boolean) =>
      edit('Osebni cilji', `${key === 'branje' ? 'Branje' : 'Šport'} ${on ? 'velja' : 'ne velja več'} za ${family.members.find((m) => m.id === memberId)?.name}`, (f) => {
        const id = `${key}-${memberId}`;
        const templates = on
          ? f.templates.some((t) => t.id === id)
            ? f.templates
            : [...f.templates, key === 'branje' ? readingTask(memberId, f.rules.readingMinutes) : sportTask(memberId, f.rules.sportMinutes)]
          : f.templates.filter((t) => t.id !== id);
        const plans = f.plans.map((p) =>
          p.memberId !== memberId
            ? p
            : {
                ...p,
                items: on
                  ? p.items.some((i) => i.key === key)
                    ? p.items
                    : [...p.items, planItem(key, key === 'branje' ? 'Branje' : 'Šport', 5, key === 'branje' ? 7 : 4, 0)]
                  : p.items.filter((i) => i.key !== key),
              }
        );
        return { ...f, templates, plans };
      }),
    [edit, family.members]
  );

  const addInstrument = useCallback(
    (memberId: string, instrument: string) =>
      edit('Osebni cilji', `Dodan instrument ${instrument} za ${family.members.find((m) => m.id === memberId)?.name}`, (f) => {
        const label = `${instrument} – domača vadba`;
        return {
          ...f,
          modules: { ...f.modules, glasba: true },
          templates: [...f.templates, instrumentTask(uid('vadba'), label, memberId)],
          plans: f.plans.map((p) =>
            p.memberId === memberId && !p.items.some((i) => i.key === 'instrument')
              ? { ...p, items: [...p.items, planItem('instrument', label, 5, 6, 0, { hint: 'vsak dan razen dneva pouka' })] }
              : p
          ),
        };
      }),
    [edit, family.members]
  );

  const updateRules = useCallback(
    (patch: Partial<FamilyRules>, area: string, text: string, effective: Effective) => commit(area, text, { target: 'rules', patch }, effective),
    [commit]
  );

  const updateRulesNow = useCallback(
    (patch: Partial<FamilyRules>, area: string, text: string) => edit(area, text, (f) => ({ ...f, rules: { ...f.rules, ...patch } })),
    [edit]
  );

  const saveAnimal = useCallback(
    (a: Animal) =>
      edit('Živali', family.animals.some((x) => x.id === a.id) ? `Urejena žival: ${a.name}` : `Dodana žival: ${a.name}`, (f) => ({
        ...f,
        animals: f.animals.some((x) => x.id === a.id) ? f.animals.map((x) => (x.id === a.id ? a : x)) : [...f.animals, a],
      })),
    [edit, family.animals]
  );

  const deleteAnimal = useCallback(
    (id: string) =>
      edit('Živali', `Odstranjena žival: ${family.animals.find((a) => a.id === id)?.name}`, (f) => ({
        ...f,
        animals: f.animals.filter((a) => a.id !== id),
        templates: f.templates.filter((t) => !(t.animalIds?.length === 1 && t.animalIds[0] === id)),
      })),
    [edit, family.animals]
  );

  const saveReward = useCallback(
    (r: Reward, effective: Effective) => {
      const prev = family.rewards.find((x) => x.id === r.id);
      if (!prev) {
        edit('Nagrade', `Nova nagrada: ${r.title} (${r.cost})`, (f) => ({ ...f, rewards: [...f.rewards, r] }));
        return;
      }
      if (JSON.stringify(prev.availableFor) !== JSON.stringify(r.availableFor) || Boolean(prev.paused) !== Boolean(r.paused)) {
        edit('Nagrade', `${r.title}: ${r.paused ? 'na pavzi' : 'aktivna'}, na voljo ${r.availableFor ? r.availableFor.length + ' otrokom' : 'vsem otrokom'}`, (f) => ({
          ...f,
          rewards: f.rewards.map((x) => (x.id === r.id ? { ...x, availableFor: r.availableFor, paused: r.paused } : x)),
        }));
      }
      if (prev.title !== r.title) edit('Nagrade', `Preimenovana nagrada: ${r.title}`, (f) => ({ ...f, rewards: f.rewards.map((x) => (x.id === r.id ? { ...x, title: r.title } : x)) }));
      if (prev.cost !== r.cost) commit('Nagrade', `${r.title}: cena ${prev.cost} → ${r.cost}`, { target: 'reward', id: r.id, patch: { cost: r.cost } }, effective);
    },
    [family.rewards, edit, commit]
  );

  /** Takojšnja sprememba vidnosti nagrade (komu je na voljo, pavza) – ne vpliva na ceno. */
  const updateReward = useCallback(
    (id: string, patch: Pick<Reward, 'availableFor' | 'paused'>, text: string) =>
      edit('Nagrade', text, (f) => ({ ...f, rewards: f.rewards.map((x) => (x.id === id ? { ...x, ...patch } : x)) })),
    [edit]
  );

  const deleteReward = useCallback(
    (id: string) => edit('Nagrade', `Odstranjena nagrada: ${family.rewards.find((r) => r.id === id)?.title}`, (f) => ({ ...f, rewards: f.rewards.filter((r) => r.id !== id) })),
    [edit, family.rewards]
  );

  const toggleModule = useCallback(
    (key: ModuleKey) => edit('Moduli', `Modul ${key}: ${family.modules[key] ? 'izklopljen' : 'vklopljen'}`, (f) => ({ ...f, modules: { ...f.modules, [key]: !f.modules[key] } })),
    [edit, family.modules]
  );

  const toggleCalendar = useCallback(
    (id: string) =>
      edit('Koledar', 'Povezava koledarja spremenjena (vzorčno)', (f) => ({
        ...f,
        calendars: f.calendars.map((c) => (c.id === id ? { ...c, status: c.status === 'vzorec' ? 'ni-povezan' : 'vzorec' } : c)),
      })),
    [edit]
  );

  const addCalendar = useCallback(
    (name: string) =>
      edit('Koledar', `Dodan vir: ${name}`, (f) => ({
        ...f,
        calendars: [...f.calendars, { id: uid('cal'), name, source: 'google-druzinski', memberIds: f.members.map((m) => m.id), status: 'ni-povezan' }],
      })),
    [edit]
  );

  const setGoal = useCallback((goal: FamilyGoal | null) => edit('Skupni cilj', goal ? `Skupni cilj: ${goal.title}` : 'Skupni cilj odstranjen', (f) => ({ ...f, goal })), [edit]);

  const createFamily = useCallback(
    (input: WizardInput, creatorRef: string): Family => {
      const fam = { ...buildFamily(input), creatorRef };
      setDb((db) => ({
        ...db,
        families: [...db.families, fam],
        changelog: [{ id: uid('ch'), familyId: fam.id, by: fam.members[0].id, at: Date.now(), area: 'Družina', text: `Družina ustvarjena (${input.start === 'prazno' ? 'prazno' : 'iz predloge'})`, effective: 'takoj', status: 'uveljavljeno' }, ...db.changelog],
      }));
      toast.success(`Družina ${fam.name} je ustvarjena. Dobrodošli!`);
      return fam;
    },
    [setDb]
  );

  return {
    renameFamily,
    saveMember,
    archiveMember,
    setInvite,
    saveTemplate,
    deleteTemplate,
    togglePersonalGoal,
    addInstrument,
    updateRules,
    updateRulesNow,
    saveAnimal,
    deleteAnimal,
    saveReward,
    updateReward,
    deleteReward,
    toggleModule,
    toggleCalendar,
    addCalendar,
    setGoal,
    applyScheduled,
    createFamily,
  };
}
